/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { ApolloError } from "@apollo/client";
import {
  GetStaticPathsResult,
  GetStaticPropsContext,
  GetStaticPropsResult,
} from "next";
import { getBrand } from "services/algolia/get-brand";
import { getProductBySlug } from "services/graphql/get-product-by-slug";
import { PromotionContextProvider } from "src/contexts/promotion.context";
import { ProductView } from "src/organisms/ProductView";
import { AlgoliaSearchBrand } from "src/types/Algolia";
import { SingleProduct } from "src/types/graphql.d";

type ProductPageProps = {
  product?: SingleProduct;
  brand?: AlgoliaSearchBrand | null;
  error: ApolloError | string | null;
  loading: boolean;
  slug: string;
};

const ProductPage = ({
  pageProps: { product, error, loading = true, slug, brand },
}: {
  pageProps: ProductPageProps;
}): JSX.Element => {
  return (
    <PromotionContextProvider>
      <ProductView
        product={product}
        productError={error}
        productLoading={loading}
        slug={slug}
        brand={brand}
      />
    </PromotionContextProvider>
  );
};

export function getStaticPaths(): GetStaticPathsResult {
  return {
    paths: [],
    fallback: "blocking",
  };
}

export async function getStaticProps({
  params,
}: GetStaticPropsContext<{
  productSlug: string;
}>): Promise<GetStaticPropsResult<ProductPageProps>> {
  if (!params || !params.productSlug) {
    return {
      notFound: true,
    };
  }

  const slug = params.productSlug;
  const product = await getProductBySlug(slug);

  if (!product) {
    return { notFound: true };
  }

  const brand = (await getBrand(product.brand.slug)) || null;

  return {
    revalidate: 60 * 15,
    props: {
      product,
      brand,
      loading: false,
      error: null,
      slug,
    },
  };
}

export default ProductPage;
